import styled, { keyframes } from "styled-components";

// Animações
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const HeaderSection = styled.header`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
`;

export const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 2rem 0;
  text-shadow: 0 4px 20px color-mix(in srgb, var(--accent-secondary) 30%, transparent);
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: 0.1s;
  animation-fill-mode: both;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const ToolbarWrap = styled.div`
  align-self: stretch;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  /* Mesmo ritmo do ContentWrapper em ViewAlerts / FormNewAlert / DetailsArea */
  padding: 2rem 2rem 0;
  margin-bottom: 2.5rem;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 0;
    margin-bottom: 2rem;
  }
`;

/** Conteúdo rolável ao lado da Sidebar (sem segundo fundo em gradiente) */
export const DetailsBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 2rem;
  box-sizing: border-box;

  ${({ $topPad }) =>
    $topPad &&
    `
    padding-top: 2rem;
  `}
`;

export const MainContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;
  padding: 0 0 2rem 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0.05;
    background-image: 
      radial-gradient(circle at 25% 25%, var(--accent-secondary) 2px, transparent 2px),
      radial-gradient(circle at 75% 75%, var(--accent) 1px, transparent 1px);
    background-size: 100px 100px, 60px 60px;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const AlertCard = styled.div`
  background: var(--surface-glass);
  backdrop-filter: blur(30px);
  border: 2px solid var(--border-default);
  border-radius: 24px;
  padding: 32px;
  width: 85%;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  box-shadow: 
    var(--shadow-md),
    inset 0 1px 0 var(--input-bg),
    0 0 0 1px var(--accent-soft);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--input-placeholder);
    box-shadow: 
      var(--shadow-lg),
      inset 0 1px 0 var(--input-bg-focus),
      0 0 0 1px var(--accent-soft);
    transform: translateY(-2px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--accent-secondary-soft) 0%, var(--accent-soft) 100%);
    opacity: 0.4;
    z-index: 1;
    border-radius: 22px;
  }

  @media (max-width: 768px) {
    padding: 24px;
    width: 90%;
  }
`;

export const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  z-index: 2;
`;

export const InfoSection = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 0;
    height: 100%;
    width: 3px;
    background: ${props => props.borderColor || 'color-mix(in srgb, var(--info) 50%, transparent)'};
    border-radius: 2px;
  }
`;

export const InfoItem = styled.div`
  margin-bottom: 8px;
  padding: 8px 0;
  border-bottom: 1px solid var(--accent-secondary-soft);
  
  &:last-child {
    border-bottom: none;
  }
`;

export const InfoLabel = styled.strong`
  color: var(--text-heading);
  font-size: 14px;
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
`;

export const InfoValue = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.4;
`;

export const TeamSection = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.35rem;
`;

export const TeamSectionTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-heading);
`;

export const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
`;

export const TeamCard = styled.div`
  background: var(--surface-2);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const TeamRole = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--accent);
`;

export const TeamName = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-heading);
`;

export const TeamContact = styled.a`
  font-size: 0.82rem;
  color: var(--text-secondary);
  text-decoration: none;
  word-break: break-all;
  margin-top: 0.15rem;
  padding: 0.2rem 0;
  border-radius: 6px;
  transition: color 0.15s ease;

  &:hover {
    color: var(--accent);
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

export const TeamEmpty = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: var(--text-muted);
`;

export const LocationContainer = styled.div`
  margin: 12px 0;
`;

export const MapsButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin: 16px 0;
  width: 100%;
  flex-wrap: wrap;
`;

export const MapContainer = styled.div`
  margin: 16px 0;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid var(--border-default);
  background: var(--surface-glass);
  
  iframe {
    width: 100%;
    height: 250px;
    border: 0;
  }
`;

export const TextAreaContainer = styled.div`
  margin: ${props => props.marginBottom ? '0 0 16px 0' : '16px 0'};
`;

export const TextAreaContent = styled.div`
  margin: 8px 0 0 0;
  padding: 16px;
  background: var(--surface-glass);
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
  min-height: 60px;
  backdrop-filter: blur(10px);

  p {
    margin: 0;
    color: var(--text-primary);
    line-height: 1.5;
    font-size: 14px;
  }
`;

export const NoDataContainer = styled.div`
  padding: 30px;
  text-align: center;
  background: var(--surface-glass);
  backdrop-filter: blur(20px);
  border: 2px solid var(--border-subtle);
  border-radius: 20px;
  
  h3 {
    margin: 0 0 12px 0;
    color: var(--text-primary);
    font-size: 18px;
  }
  
  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: 14px;
  }
`;

export const MapsLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: var(--accent-secondary-soft);
  color: var(--text-heading);
  text-decoration: none;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: 2px solid var(--border-default);
  backdrop-filter: blur(10px);

  &:hover {
    background: var(--accent-secondary-soft);
    border-color: var(--accent);
    transform: translateY(-2px);
  }
`;

export const StyledButton = styled.button`
  position: relative;
  background: var(--accent-soft);
  backdrop-filter: blur(20px);
  border: 2px solid var(--accent-soft);
  border-radius: 50px;
  padding: 1rem 3rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--accent-secondary);
  font-weight: 600;
  font-size: 1rem;
  margin: 3rem 0 2rem 0;
  min-width: 150px;
  height: 50px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--accent-secondary-soft) 0%, var(--accent-soft) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 50px;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50px;
    background: transparent;
    box-shadow: 0 8px 25px var(--accent-soft);
    opacity: 0;
    transition: all 0.4s ease;
    z-index: -1;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover::after {
    opacity: 1;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: var(--accent);
    color: var(--accent-secondary);
    box-shadow: inset 0 1px 0 var(--surface-glass-strong);
  }

  &:active {
    transform: translateY(-2px);
  }

  > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    padding: 0.8rem 2rem;
    font-size: 0.9rem;
    min-width: 120px;
  }

  @media (max-width: 480px) {
    padding: 0.7rem 1.5rem;
    font-size: 0.8rem;
    min-width: 100px;
  }
`;

export const RegisterActionFab = styled.button`
  padding: 10px 18px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.8125rem;
 background: ${props => {
    if (props.$active) {
      return 'var(--surface-1)';
    }
    return 'var(--brand-lime)';
  }};
  
  color: ${props => {
    if (props.$active) {
      return 'var(--text-secondary)';
    }
    return 'var(--text-on-lime)';
  }};
  border: 2px solid var(--text-heading);
  box-shadow: var(--shadow-md);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 26px var(--accent-soft);
  }
`;

export const InfoRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

export const InfoColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

// ========================================
// NOVOS ESTILOS PARA O RELATÓRIO
// ========================================

export const ReportContainer = styled.div`
  width: 85%;
  max-width: 1200px;
  margin: 40px auto 0;
`;

export const ReportHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

export const ReportTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const ReportSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
`;

export const TimelineContainer = styled.div`
  position: relative;
  padding-left: 40px;

  @media (max-width: 768px) {
    padding-left: 30px;

    &::before {
      left: 14px;
    }
  }
`;

export const TicketCard = styled.div`
  position: relative;
  background: var(--surface-glass);
  backdrop-filter: blur(30px);
  border: 2px solid ${props => props.borderColor || 'var(--border-default)'};
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  transition: all 0.3s ease;
  box-shadow: 
    var(--shadow-sm),
    inset 0 1px 0 var(--input-bg);

  @media (max-width: 768px) {
    padding: 20px;
    
    &::before {
      left: -30px;
      width: 10px;
      height: 10px;
    }
  }
`;

export const TicketHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--accent-secondary-soft);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

export const StepBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, var(--accent-secondary-soft) 0%, var(--accent-soft) 100%);
  border: 1px solid var(--border-default);
  border-radius: 20px;
  font-weight: 600;
  color: var(--accent-secondary);
  font-size: 14px;
`;

export const StepNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--accent-soft);
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
`;

export const DateBadge = styled.div`
  padding: 6px 14px;
  background: var(--surface-overlay);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
`;

export const TicketContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ContentBlock = styled.div`
  background: var(--surface-glass);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid var(--accent-secondary-soft);
`;

export const ContentLabel = styled.div`
  color: var(--text-heading);
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;


`;

export const ContentText = styled.p`
  margin: 0;
  color: var(--text-primary);
  line-height: 1.6;
  font-size: 14px;
`;

export const PhotoTypeBadge = styled.div`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
  background-color: ${({ $type }) => {
    switch ($type) {
      case 'natural': return 'color-mix(in srgb, var(--success) 22%, transparent)';
      case 'antropico': return 'color-mix(in srgb, var(--warning) 22%, transparent)';
      case 'intencional': return 'color-mix(in srgb, var(--danger) 22%, transparent)';
      default: return 'color-mix(in srgb, var(--info) 22%, transparent)';
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case 'natural': return 'var(--success)';
      case 'antropico': return 'var(--warning)';
      case 'intencional': return 'var(--danger)';
      default: return 'var(--info)';
    }
  }};
`;

export const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;   // antes era 12px
  margin-top: 16px;
  width: 100%;
`;

export const Image = styled.img`
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 8px;
`;


// Imagem clicável (adiciona cursor pointer)
export const ClickableImage = styled(Image)`
  cursor: pointer;
`;

// Container do preview do documento (arquivo não-imagem)
export const DocumentPreview = styled.div`
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  padding: 16px;
  background-color: var(--surface-2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

export const DocumentIcon = styled.span`
  font-size: 32px;
`;

export const DocumentDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const DocumentInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DocumentName = styled.div`
  font-weight: bold;
  font-size: 14px;
`;

export const DocumentTypeLabel = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

export const DocumentDownloadLink = styled.a`
  background-color: var(--info);
  color: var(--text-on-accent);
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--info);
  }
`;


// MODAL
export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;



export const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ==============================
// CORREÇÃO / COMPLEMENTO DOS ESTILOS EXISTENTES
// ==============================
export const RejectButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 0 6px;
`;

export const RejectButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 18px;
  border-radius: 40px;
  border: 1.5px solid color-mix(in srgb, var(--danger) 20%, transparent);
  background: color-mix(in srgb, var(--danger) 20%, transparent);
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--danger);
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);

  &:hover {
    background: color-mix(in srgb, var(--danger) 20%, transparent);
    border-color: var(--danger);
    color: var(--danger);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(1px);
  }

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
  }
`;


export const StyledButtonVoltar = styled.button`
  border: 1px solid ${props => {
    if (props.$isRead) {
      return 'var(--border-default)';
    }
    return 'var(--border-subtle)';
  }};
  
  background: ${props => {
    if (props.$isRead) {
      return 'var(--surface-2)';
    }
    return 'var(--brand-lime)';
  }};
  
  color: ${props => {
    if (props.$isRead) {
      return 'var(--text-muted)';
    }
    return 'var(--text-on-lime)';
  }};
  border-radius: 16px;
  cursor: pointer;
  padding: 12px 24px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 44px;
  z-index: 2;

  &:hover {
    transform: translateY(-3px);
    border-color: var(--accent);
    color: var(--text-primary);
  }

  &:active { transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;